import React, { useState, useRef, useEffect } from "react";
import { Redirect } from "react-router-dom";
import { Form, Icon, Input, Button, message, Spin, Tabs } from "antd";
import { connect } from "react-redux";
import DocumentTitle from "react-document-title";
import "./index.less";
import { login } from "@/store/actions";
import { sendStaffCaptcha } from "@/api/login";
import menuList from "@/config/menuConfig";

const { TabPane } = Tabs;

// 根据用户角色找到第一个有权限访问的菜单项
const findFirstAccessibleRoute = (role) => {
  // 递归查找菜单项
  const findRoute = (items) => {
    for (const item of items) {
      // 检查当前菜单项是否有权限访问
      if (!item.roles || item.roles.includes(role)) {
        // 如果有子菜单，先检查子菜单
        if (item.children && item.children.length > 0) {
          const childRoute = findRoute(item.children);
          if (childRoute) return childRoute;
        }
        // 返回当前菜单项的路径
        return item.path;
      }
    }
    return null;
  };
  
  // 从菜单配置中查找
  const route = findRoute(menuList);
  return route || "/dashboard"; // 如果没找到，默认返回dashboard
};

const Login = (props) => {
  const { form, token, login, role } = props;
  const { getFieldDecorator } = form;

  const [loading, setLoading] = useState(false);
  const [loginType, setLoginType] = useState('account');
  const [captchaSent, setCaptchaSent] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const isMounted = useRef(true);

  // 添加清理函数
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // 安全的setState函数
  const safeSetState = (setter) => {
    if (isMounted.current) {
      setter();
    }
  };

  // 登录处理函数
  const handleLogin = async (credentials) => {
    safeSetState(() => setLoading(true));
    
    try {
      const response = await login(credentials);
      
      if (response && response.status === 0) {
        message.success("登录成功，正在跳转...");
      } else {
        message.error(response.message || "登录失败");
      }
    } catch (error) {
      message.error(error || "登录失败，请重试");
    } finally {
      safeSetState(() => setLoading(false));
    }
  };

  // 处理登录类型切换
  const handleTabChange = (activeKey) => {
    safeSetState(() => setLoginType(activeKey));
    
    // 清除当前tab不相关的字段验证错误
    const fieldsToReset = activeKey === 'account' 
      ? ['phone', 'captcha']
      : ['username', 'password'];
    
    // 重置字段验证状态
    form.resetFields(fieldsToReset);
  };

  // 表单提交处理
  const handleSubmit = (event) => {
    event.preventDefault();
    
    // 根据登录类型决定要验证的字段
    const fieldsToValidate = loginType === 'account' 
      ? ['username', 'password']
      : ['phone', 'captcha'];
    
    form.validateFields(fieldsToValidate, (err, values) => {
      if (!err) {
        const credentials = loginType === 'account'
          ? { 
              username: values.username?.trim(), 
              password: values.password 
            }
          : { 
              phone: values.phone?.trim(), 
              captcha: values.captcha 
            };
        
        // 验证必填字段
        if (loginType === 'account' && (!credentials.username || !credentials.password)) {
          message.error('请输入用户名和密码');
          return;
        }
        
        if (loginType === 'phone' && (!credentials.phone || !credentials.captcha)) {
          message.error('请输入手机号和验证码');
          return;
        }
        
        handleLogin(credentials);
      } else {
        // 显示具体的验证错误信息
        const errorFields = Object.keys(err);
        const firstError = err[errorFields[0]];
        if (firstError && firstError.errors && firstError.errors.length > 0) {
          message.error(firstError.errors[0].message);
        } else {
          message.error("请检查输入信息");
        }
      }
    });
  };

  // 发送验证码
  const handleSendCaptcha = () => {
    form.validateFields(['phone'], (err, values) => {
      if (!err) {
        const { phone } = values;
        safeSetState(() => setLoading(true));
        
        sendStaffCaptcha({ phone })
          .then(() => {
            if (isMounted.current) {
              message.success('验证码已发送');
              safeSetState(() => setCaptchaSent(true));
              startCountdown();
            }
          })
          .catch(error => {
            if (isMounted.current) {
              message.error(error.message || '发送验证码失败');
            }
          })
          .finally(() => {
            safeSetState(() => setLoading(false));
          });
      }
    });
  };

  // 倒计时
  const startCountdown = () => {
    let count = 60;
    safeSetState(() => setCountdown(count));
    const timer = setInterval(() => {
      count--;
      if (isMounted.current) {
        safeSetState(() => setCountdown(count));
        if (count === 0) {
          clearInterval(timer);
          safeSetState(() => setCaptchaSent(false));
        }
      } else {
        clearInterval(timer);
      }
    }, 1000);
  };

  // 如果已登录且有有效角色，跳转到有权限的第一个菜单项
  if (token && role && role.trim() !== '') {
    const redirectPath = findFirstAccessibleRoute(role);
    return <Redirect to={redirectPath} />;
  }

  return (
    <DocumentTitle title={"用户登录"}>
      <div className="login-container">
        <Form onSubmit={handleSubmit} className="content">
          <div className="title">
            <h2>用户登录</h2>
          </div>
          <Spin spinning={loading} tip="处理中...">
            <Tabs activeKey={loginType} onChange={handleTabChange}>
              <TabPane tab="账号密码登录" key="account">
                <Form.Item>
                  {getFieldDecorator("username", {
                    rules: [
                      {
                        required: true,
                        message: "请输入用户名",
                      },
                    ],
                  })(
                    <Input
                      prefix={
                        <Icon type="user" style={{ color: "rgba(0,0,0,.25)" }} />
                      }
                      placeholder="用户名"
                    />
                  )}
                </Form.Item>
                <Form.Item>
                  {getFieldDecorator("password", {
                    rules: [
                      {
                        required: true,
                        message: "请输入密码",
                      },
                    ],
                  })(
                    <Input
                      prefix={
                        <Icon type="lock" style={{ color: "rgba(0,0,0,.25)" }} />
                      }
                      type="password"
                      placeholder="密码"
                    />
                  )}
                </Form.Item>
              </TabPane>
              <TabPane tab="手机号登录" key="phone">
                <Form.Item>
                  {getFieldDecorator("phone", {
                    rules: [
                      {
                        required: true,
                        message: "请输入手机号",
                      },
                      {
                        pattern: /^1\d{10}$/,
                        message: "请输入正确的手机号",
                      },
                    ],
                  })(
                    <Input
                      prefix={
                        <Icon type="mobile" style={{ color: "rgba(0,0,0,.25)" }} />
                      }
                      placeholder="手机号"
                    />
                  )}
                </Form.Item>
                <Form.Item>
                  <div style={{ display: 'flex' }}>
                    {getFieldDecorator("captcha", {
                      rules: [
                        {
                          required: true,
                          message: "请输入验证码",
                        },
                      ],
                    })(
                      <Input
                        prefix={
                          <Icon type="safety" style={{ color: "rgba(0,0,0,.25)" }} />
                        }
                        placeholder="验证码"
                        style={{ flex: 1, marginRight: 10 }}
                      />
                    )}
                    <Button 
                      disabled={captchaSent || loading} 
                      onClick={handleSendCaptcha}
                    >
                      {captchaSent ? `${countdown}s后重试` : '发送验证码'}
                    </Button>
                  </div>
                </Form.Item>
              </TabPane>
            </Tabs>
            
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                className="login-form-button"
                loading={loading}
              >
                登录
              </Button>
            </Form.Item>
          </Spin>
        </Form>
      </div>
    </DocumentTitle>
  );
};

const WrapLogin = Form.create()(Login);

export default connect((state) => state.user, { login })(WrapLogin);
